import { MatDialog } from '@angular/material/dialog';
import { ShareToWorkspaceDialogComponent } from '../share-to-workspace/share-to-workspace-dialog.component';
import { Component, OnInit } from "@angular/core";
import { AppService } from "../app.service";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";

@Component({
    selector: "app-recipe",
    standalone: false,
    templateUrl: "./recipe.component.html",
    styleUrls: ["./recipe.component.css"],
})
export class RecipeComponent implements OnInit {
    recipelist: any = [];
    apiKey: any;
    userKey: any;
    showErrorMsg: boolean = false;
    showErrorText: any;
    status: any;
    workspaceName: any;
    txt: any;
    loading = true;

    // Token filter properties
    selectedTokens: string[] = [];
    isFilteredByToken: boolean = false;

    // Tag filter properties
    selectedTag: string = "";
    isFilteredByTag: boolean = false;

    // Popup properties (forget recipe)
    showForgetPopup: boolean = false;
    selectedRecipe: any = null;
    popupPosition: any = { top: 0, left: 0 };

    // Pagination properties
    currentPage: number = 1;
    itemsPerPage: number = 25;
    totalRecipes: number = 0;
    totalPages: number = 0;
    jumpToPageNumber: number = 1;

    // Search and filter
    searchQuery: string = "";
    filteredRecipes: any[] = [];
    paginatedRecipes: any[] = [];

    // Sort
    sortBy: string = "date-desc";

    // View mode
    viewMode: "grid" | "list" | "compact" = "grid";

    constructor(
        public appService: AppService,
        public router: Router,
        private http: HttpClient,
        private route: ActivatedRoute,
        private dialog: MatDialog
    ) {
        this.userKey = this.route.snapshot.paramMap.get("key");
    }

      /**
       * Put this recipe into one or more shared workspaces.
       *
       * The item does not move: XDB adds the workspace keys to the record that is
       * already here, and the connector resolves those keys from workspaces this
       * account owns. Nothing about the key travels through the browser.
       */
      shareRecipe(event: Event, recipe: any): void {
        event.stopPropagation();

        const workspaceId = this.currentWorkspaceId();
        if (!workspaceId) {
          this.appService.addWarnMessage(
            'Pick a workspace first - this page does not know which one you are in.');
          return;
        }

        this.dialog.open(ShareToWorkspaceDialogComponent, {
          width: '460px',
          data: {
            type: 'RECIPE',
            // Memories are identified by transactionNumber on this screen and
            // recipes by queryHash; XDB's shareMemory takes queryHash. Prefer the
            // real one and fall back rather than silently sending nothing.
            queryHash: recipe?.queryHash || recipe?.transactionNumber,
            sourceWorkspaceId: workspaceId,
            userKey: this.userKey,
            preview: recipe?.memory,
          },
        });
      }

      /** Id of the workspace this screen is showing, from the stored selection. */
      private currentWorkspaceId(): number | null {
        try {
          const raw = this.appService.get('xdbc.workspace');
          if (!raw || typeof raw !== 'string') {
            return null;
          }
          const ws = JSON.parse(raw);
          return ws?.workspaceId || ws?.id || null;
        } catch {
          return null;
        }
      }

    ngOnInit(): void {
        this.loading = true;

        // Load view mode from localStorage
        const savedViewMode = localStorage.getItem("recipeViewMode");
        if (savedViewMode) {
            this.viewMode = savedViewMode as "grid" | "list" | "compact";
        }

        // Load items per page from localStorage
        const savedItemsPerPage = localStorage.getItem("recipeItemsPerPage");
        if (savedItemsPerPage) {
            this.itemsPerPage = parseInt(savedItemsPerPage, 10);
        }

        // Check for tag or token query parameters
        this.route.queryParams.subscribe((params) => {
            this.selectedTag = params["tag"] || "";
            this.isFilteredByTag = !!this.selectedTag;

            const tokenParam = params["token"];
            if (tokenParam) {
                this.selectedTokens = [tokenParam];
                this.isFilteredByToken = true;
            } else {
                this.selectedTokens = [];
                this.isFilteredByToken = false;
            }

            this.listRecipes();
        });
    }

    async listRecipes() {
        try {
            const wsJson = this.appService.get("xdbc.workspace");
            if (
                wsJson !== undefined &&
                typeof wsJson === "string" &&
                wsJson.trim() !== "" &&
                wsJson !== "undefined"
            ) {
                try {
                    const ws = JSON.parse(wsJson);
                    this.apiKey = ws.apiKey;
                    this.status = ws.status;
                    this.workspaceName = ws.workspaceName;
                } catch (err) {
                    console.error("Invalid JSON:", err);
                }
            }

            if (this.status === "ACTIVATED") {
                this.showErrorMsg = false;
            } else {
                this.showErrorMsg = true;
                this.showErrorText =
                    "The selected workspace is currently inactive. Please activate it to proceed.";
            }

            const activationPayload: any = {
                userKey: this.userKey,
                tokens: this.isFilteredByToken ? this.selectedTokens : [],
                query: "",
                page: 0,
                queryType: "RECIPE",
                routeType: "UI_SESSION",
            };

            const signatureData = await this.appService.wssign(
                activationPayload,
                this.workspaceName
            );

            if (!signatureData) {
                throw new Error("Failed to sign activation payload");
            }

            const signature = new TextDecoder().decode(signatureData);

            const response = await this.appService.wsPostRequestWithHeaders(
                activationPayload,
                "recipe/list-recipe",
                this.apiKey,
                signature
            );

            this.loading = false;
            if (response && response.status == "Success") {
                // Normalize: ensure steps are sorted by order for each recipe
                this.recipelist = (response?.data?.memories || []).map((r: any) => ({
                    ...r,
                    steps: (r.steps || [])
                        .slice()
                        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
                }));
                this.totalRecipes =
                    response?.data?.totalActiveMemories ?? this.recipelist.length;
                this.updatePagination();
                this.txt = response.message;
            } else {
                throw new Error(response?.message);
            }
        } catch (error: any) {
            this.loading = false;
            this.txt = "Failed to load recipes: " + (error.message || "Unknown error");
        }
    }

    // ==================== Forget Recipe ====================
    showForgetRecipePopup(event: MouseEvent, recipe: any) {
        event.stopPropagation();

        const rect = (event.target as HTMLElement).getBoundingClientRect();
        this.popupPosition = {
            top: rect.top + window.scrollY - 10,
            left: rect.left + window.scrollX - 200,
        };

        this.selectedRecipe = recipe;
        this.showForgetPopup = true;
    }

    closeForgetPopup() {
        this.showForgetPopup = false;
        this.selectedRecipe = null;
    }

    async forgetRecipe() {
        try {
            if (!this.selectedRecipe) return;

            const wsJson = this.appService.get("xdbc.workspace");
            if (
                wsJson !== undefined &&
                typeof wsJson === "string" &&
                wsJson.trim() !== "" &&
                wsJson !== "undefined"
            ) {
                try {
                    const ws = JSON.parse(wsJson);
                    this.apiKey = ws.apiKey;
                    this.workspaceName = ws.workspaceName;
                } catch (err) {
                    console.error("Invalid JSON:", err);
                }
            }

            const forgetPayload = {
                userKey: this.userKey,
                queryHash: this.selectedRecipe.queryHash,
                queryType: "RECIPE",
                routeType: "UI_SESSION",
            };

            const signatureData = await this.appService.wssign(
                forgetPayload,
                this.workspaceName
            );

            if (!signatureData) {
                throw new Error("Failed to sign forget payload");
            }

            const signature = new TextDecoder().decode(signatureData);

            // NOTE: confirm the actual delete endpoint name with your backend team.
            const response = await this.appService.wsPostRequestWithHeaders(
                forgetPayload,
                "recipe/forget",
                this.apiKey,
                signature
            );

            if (response && response.status == "Success") {
                this.recipelist = this.recipelist.filter(
                    (r: any) => r.queryHash !== this.selectedRecipe.queryHash
                );
                this.totalRecipes = this.recipelist.length;
                this.txt = "Recipe forgotten successfully";
                this.updatePagination();
            } else {
                throw new Error(response?.message || "Failed to forget recipe");
            }
        } catch (error: any) {
            this.txt =
                "Failed to forget recipe: " + (error.message || "Unknown error");
        } finally {
            this.closeForgetPopup();
        }
    }

    // ==================== Filtering ====================
    filterByToken(token: string) {
        if (token) {
            this.router.navigate(["/recipe", this.userKey], {
                queryParams: { token: token },
            });
        }
    }

    filterByTag(tag: string) {
        if (tag) {
            this.router.navigate(["/recipe", this.userKey], {
                queryParams: { tag: tag },
            });
        }
    }

    clearFilter() {
        this.router.navigate(["/recipe", this.userKey]);
    }

    // ==================== Pagination ====================
    /** See the note on MemoriesComponent.ownerFilter - same field, same rule. */
    ownerFilter: 'ALL' | 'MINE' | 'SHARED' = 'ALL';

    setOwnerFilter(value: 'ALL' | 'MINE' | 'SHARED') {
        this.ownerFilter = value;
        this.currentPage = 1;
        this.updatePagination();
    }

    get sharedCount(): number {
        return this.recipelist.filter((r: any) => r?.isShared).length;
    }

    get ownCount(): number {
        return this.recipelist.length - this.sharedCount;
    }

    updatePagination() {
        // Apply search filter (matches recipe name, step text, or tokens)
        this.filteredRecipes = this.recipelist.filter((recipe: any) => {
            if (this.ownerFilter === 'SHARED' && !recipe?.isShared) return false;
            if (this.ownerFilter === 'MINE' && recipe?.isShared) return false;

            let passesTag = true;
            let passesSearch = true;

            if (this.isFilteredByTag) {
                passesTag = recipe.notesKey === this.selectedTag;
            }

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                passesSearch =
                    recipe.memory?.toLowerCase().includes(query) ||
                    recipe.tokens?.some((token: string) =>
                        token.toLowerCase().includes(query)
                    ) ||
                    recipe.steps?.some((step: any) =>
                        step.text?.toLowerCase().includes(query)
                    );
            }

            return passesTag && passesSearch;
        });

        // Apply sorting
        this.applySorting();

        // Calculate pagination
        this.totalPages = Math.ceil(
            this.filteredRecipes.length / this.itemsPerPage
        );

        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = 1;
        }

        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedRecipes = this.filteredRecipes.slice(startIndex, endIndex);
    }

    applySorting() {
        switch (this.sortBy) {
            case "date-desc":
                this.filteredRecipes.sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                break;
            case "date-asc":
                this.filteredRecipes.sort(
                    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                break;
            case "steps-desc":
                this.filteredRecipes.sort(
                    (a, b) => (b.steps?.length || 0) - (a.steps?.length || 0)
                );
                break;
            case "steps-asc":
                this.filteredRecipes.sort(
                    (a, b) => (a.steps?.length || 0) - (b.steps?.length || 0)
                );
                break;
        }
    }

    onSearchChange() {
        this.currentPage = 1;
        this.updatePagination();
    }

    clearSearch() {
        this.searchQuery = "";
        this.onSearchChange();
    }

    onItemsPerPageChange() {
        localStorage.setItem("recipeItemsPerPage", this.itemsPerPage.toString());
        this.currentPage = 1;
        this.updatePagination();
    }

    onSortChange() {
        this.updatePagination();
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.updatePagination();
            this.scrollToTop();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updatePagination();
            this.scrollToTop();
        }
    }

    goToFirstPage() {
        this.currentPage = 1;
        this.updatePagination();
        this.scrollToTop();
    }

    goToLastPage() {
        this.currentPage = this.totalPages;
        this.updatePagination();
        this.scrollToTop();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== -1) {
            this.currentPage = page;
            this.updatePagination();
            this.scrollToTop();
        }
    }

    jumpToPage() {
        if (
            this.jumpToPageNumber >= 1 &&
            this.jumpToPageNumber <= this.totalPages
        ) {
            this.currentPage = this.jumpToPageNumber;
            this.updatePagination();
            this.scrollToTop();
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 7;

        if (this.totalPages <= maxPagesToShow) {
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (this.currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push(-1);
                pages.push(this.totalPages);
            } else if (this.currentPage >= this.totalPages - 3) {
                pages.push(1);
                pages.push(-1);
                for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push(-1);
                for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push(-1);
                pages.push(this.totalPages);
            }
        }

        return pages;
    }

    getStartIndex(): number {
        return (this.currentPage - 1) * this.itemsPerPage;
    }

    getEndIndex(): number {
        const end = this.currentPage * this.itemsPerPage;
        return Math.min(end, this.filteredRecipes.length);
    }

    scrollToTop() {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // ==================== View mode ====================
    setViewMode(mode: "grid" | "list" | "compact") {
        this.viewMode = mode;
        localStorage.setItem("recipeViewMode", mode);
    }

    async copyMessage(message: string) {
        try {
            await navigator.clipboard.writeText(message);
        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    }
}